import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, axios } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

// ── Import History Tab ──
function ImportHistoryTab({ isAdmin }) {
  const [history, setHistory]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [deleting, setDeleting] = useState(null);

  const loadHistory = async () => {
    setLoading(true);
    try { const { data } = await axios.get('/voters/import-history'); setHistory(data); }
    catch { toast.error('Failed to load history'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadHistory(); }, []);

  const handleDelete = async (batchId, filename, count) => {
    if (!window.confirm(`Delete "${filename}"?\nThis will remove ${count} voter records permanently!`)) return;
    setDeleting(batchId);
    try {
      await axios.delete(`/voters/import-history/${batchId}`);
      toast.success(`✅ Deleted ${count} voters from "${filename}"`);
      loadHistory();
    } catch(err) { toast.error(err.response?.data?.error || 'Delete failed'); }
    finally { setDeleting(null); }
  };

  const fmt = ts => new Date(String(ts).replace(' ','T')+(String(ts).includes('Z')?'':'Z')).toLocaleDateString('en-IN', { timeZone:'Asia/Kolkata', day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });

  return (
    <div style={{ background:'rgba(255,255,255,0.9)', backdropFilter:'blur(12px)', borderRadius:20, padding:'20px', boxShadow:'0 4px 24px rgba(255,107,0,0.08)', border:'1px solid rgba(255,255,255,0.7)' }}>
      <div style={{ fontFamily:"'Baloo 2',sans-serif", fontWeight:700, fontSize:'1rem', color:'#BF360C', marginBottom:16 }}>📋 Import History</div>

      {loading ? (
        <div style={{ textAlign:'center', padding:32, color:'#FF6B00' }}>Loading...</div>
      ) : history.length === 0 ? (
        <div style={{ textAlign:'center', padding:32, color:'#9E6040' }}>
          <div style={{ fontSize:'2.5rem', marginBottom:10 }}>📭</div>
          <div style={{ fontWeight:700 }}>No import history found</div>
          <div style={{ fontSize:'0.8rem', marginTop:4 }}>Import an Excel file to see it here</div>
        </div>
      ) : (
        <div>
          {history.map(h => (
            <div key={h.batch_id} style={{ background:'white', borderRadius:14, padding:'14px 16px', marginBottom:10, border:'1.5px solid rgba(255,107,0,0.12)', boxShadow:'0 2px 10px rgba(0,0,0,0.05)' }}>
              <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
                <div style={{ width:42, height:42, borderRadius:10, background:'linear-gradient(135deg,#E55100,#FF6B00)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.2rem', flexShrink:0 }}>📥</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:700, fontSize:'0.9rem', color:'#1A0A00', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{h.filename}</div>
                  <div style={{ fontSize:'0.75rem', color:'#9E6040', marginTop:2 }}>Imported by: <strong>{h.imported_by_name||'Unknown'}</strong></div>
                  <div style={{ fontSize:'0.72rem', color:'#9E6040' }}>🕐 {fmt(h.imported_at)}</div>
                  <div style={{ display:'flex', gap:8, marginTop:6, flexWrap:'wrap' }}>
                    <span style={{ background:'#E8F5E9', color:'#2E7D32', padding:'2px 8px', borderRadius:99, fontSize:'0.7rem', fontWeight:700 }}>✅ {h.imported} imported</span>
                    {h.skipped>0 && <span style={{ background:'#FFF9C4', color:'#F57F17', padding:'2px 8px', borderRadius:99, fontSize:'0.7rem', fontWeight:700 }}>⏭️ {h.skipped} skipped</span>}
                    {h.district_info && <span style={{ background:'#E3F2FD', color:'#1565C0', padding:'2px 8px', borderRadius:99, fontSize:'0.7rem', fontWeight:700 }}>🗺️ {h.district_info}</span>}
                  </div>
                </div>
                {isAdmin && (
                  <button onClick={()=>handleDelete(h.batch_id, h.filename, h.imported)} disabled={deleting===h.batch_id}
                    style={{ background:deleting===h.batch_id?'#ccc':'linear-gradient(135deg,#C62828,#E53935)', border:'none', borderRadius:10, padding:'8px 12px', cursor:deleting===h.batch_id?'not-allowed':'pointer', color:'white', fontSize:'0.78rem', fontWeight:700, flexShrink:0 }}>
                    {deleting===h.batch_id ? '...' : '🗑️ Delete'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Printer Tab: Booth → Voters → Print ──
function PrinterTab({ navigate }) {
  const [step, setStep]         = useState('booths'); // 'booths' | 'voters' | 'search'
  const [booths, setBooths]     = useState([]);
  const [voters, setVoters]     = useState([]);
  const [selectedBooth, setSelectedBooth] = useState(null);
  const [loadingBooths, setLoadingBooths] = useState(true);
  const [loadingVoters, setLoadingVoters] = useState(false);
  const [page, setPage]         = useState(1);
  const [pages, setPages]       = useState(1);
  const [total, setTotal]       = useState(0);
  const [searchName, setSearchName] = useState('');

  useEffect(() => {
    axios.get('/stats/booths').then(r => setBooths(r.data)).catch(()=>{}).finally(()=>setLoadingBooths(false));
  }, []);

  const loadVoters = async (boothNumber, p=1) => {
    setLoadingVoters(true);
    try {
      const { data } = await axios.get(`/voters/booth/${boothNumber}`, { params:{ page:p, limit:50 } });
      setVoters(data.voters || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(p);
      setSelectedBooth(boothNumber);
      setStep('voters');
    } catch { toast.error('Failed to load voters'); }
    finally { setLoadingVoters(false); }
  };

  const handleSearch = async () => {
    if (!searchName.trim()) { toast.error('Enter a name'); return; }
    setLoadingVoters(true);
    try {
      const { data } = await axios.get('/voters/search', { params:{ name: searchName, limit:50 } });
      setVoters(data.voters || []);
      setTotal(data.total || 0);
      setPages(1); setPage(1);
      setStep('voters');
      setSelectedBooth(null);
      if (!data.voters?.length) toast('No voters found', { icon:'🔍' });
    } catch { toast.error('Search failed'); }
    finally { setLoadingVoters(false); }
  };

  const filteredBooths = booths.filter(b =>
    !searchName || b.booth_number?.toString().includes(searchName) ||
    b.booth_address?.toLowerCase().includes(searchName.toLowerCase()) ||
    b.area?.toLowerCase().includes(searchName.toLowerCase())
  );

  return (
    <div style={{ background:'rgba(255,255,255,0.9)', backdropFilter:'blur(12px)', borderRadius:20, padding:'20px', boxShadow:'0 4px 24px rgba(255,107,0,0.08)', border:'1px solid rgba(255,255,255,0.7)' }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
        {step==='voters' && (
          <button onClick={()=>{ setStep('booths'); setVoters([]); setSearchName(''); }} style={{ background:'rgba(255,107,0,0.1)', border:'none', borderRadius:8, width:32, height:32, cursor:'pointer', fontSize:'1rem', display:'flex', alignItems:'center', justifyContent:'center' }}>←</button>
        )}
        <div style={{ fontFamily:"'Baloo 2',sans-serif", fontWeight:700, fontSize:'1rem', color:'#BF360C' }}>
          {step==='booths' ? '🖨️ Select Booth to Print' : `🏫 Booth ${selectedBooth||'Search Results'} — ${total} voters`}
        </div>
      </div>

      {/* Search bar */}
      {step==='booths' && (
        <div style={{ display:'flex', gap:8, marginBottom:14 }}>
          <input
            style={{ flex:1, padding:'9px 12px', border:'2px solid rgba(255,107,0,0.2)', borderRadius:10, fontSize:'0.85rem', fontFamily:"'Outfit',sans-serif", outline:'none' }}
            placeholder="Search by name, booth no or area..."
            value={searchName}
            onChange={e=>setSearchName(e.target.value)}
            onKeyDown={e=>e.key==='Enter'&&handleSearch()}
          />
          <button onClick={handleSearch} style={{ padding:'9px 14px', border:'none', borderRadius:10, background:'linear-gradient(135deg,#E55100,#FF6B00)', color:'white', fontWeight:700, fontSize:'0.85rem', cursor:'pointer' }}>
            🔍
          </button>
        </div>
      )}

      {/* BOOTH LIST */}
      {step==='booths' && (
        loadingBooths ? <div style={{ textAlign:'center', padding:24, color:'#FF6B00' }}>Loading booths...</div> :
        <div style={{ maxHeight:380, overflowY:'auto' }}>
          {filteredBooths.map((b, idx) => (
            <div key={`${b.booth_number}-${idx}`} onClick={()=>loadVoters(b.booth_number)}
              style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', borderRadius:12, marginBottom:8, background:'linear-gradient(135deg,#FFF3E0,#FFE0B2)', border:'1.5px solid rgba(255,107,0,0.2)', cursor:'pointer', transition:'all 0.2s' }}
              onMouseEnter={e=>e.currentTarget.style.transform='translateX(4px)'}
              onMouseLeave={e=>e.currentTarget.style.transform='none'}>
              <div style={{ width:40, height:40, borderRadius:10, background:'linear-gradient(135deg,#E55100,#FF6B00)', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:800, fontSize:'0.9rem', flexShrink:0 }}>
                {b.booth_number}
              </div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:'0.88rem', color:'#1A0A00' }}>{b.booth_address || `Booth ${b.booth_number}`}</div>
                {b.area && <div style={{ fontSize:'0.74rem', color:'#9E6040' }}>📍 {b.area}</div>}
              </div>
              <div style={{ textAlign:'right', flexShrink:0 }}>
                <div style={{ fontWeight:800, color:'#E55100', fontSize:'1rem' }}>{b.total||0}</div>
                <div style={{ fontSize:'0.68rem', color:'#9E6040' }}>{b.male||0}M/{b.female||0}F</div>
              </div>
              <span style={{ color:'#FF6B00', fontSize:'1.1rem' }}>›</span>
            </div>
          ))}
        </div>
      )}

      {/* VOTER LIST */}
      {step==='voters' && (
        loadingVoters ? <div style={{ textAlign:'center', padding:24, color:'#FF6B00' }}>Loading voters...</div> :
        <div>
          {/* Column header */}
          <div style={{ display:'grid', gridTemplateColumns:'50px 1fr 70px 60px', gap:8, padding:'6px 10px', background:'rgba(255,107,0,0.06)', borderRadius:8, fontSize:'0.68rem', fontWeight:700, color:'#BF360C', marginBottom:8 }}>
            <span>S.No</span><span>Name</span><span>Sex/Age</span><span>Print</span>
          </div>
          <div style={{ maxHeight:360, overflowY:'auto' }}>
            {voters.map(v => (
              <div key={v.id} style={{ display:'grid', gridTemplateColumns:'50px 1fr 70px 60px', gap:8, padding:'10px', borderRadius:10, marginBottom:6, background:'rgba(255,255,255,0.9)', border:'1px solid rgba(255,107,0,0.1)', alignItems:'center' }}>
                <div style={{ fontWeight:700, color:'#E55100', fontSize:'0.85rem', textAlign:'center' }}>{v.serial_number||'—'}</div>
                <div>
                  <div style={{ fontWeight:700, fontSize:'0.85rem', color:'#1A0A00' }}>{v.name}</div>
                  <div style={{ fontSize:'0.72rem', color:'#9E6040' }}>{v.relation_name||'—'}</div>
                </div>
                <div style={{ textAlign:'center' }}>
                  <div className={`badge badge-${v.gender==='M'?'m':'f'}`}>{v.gender}/{v.age}</div>
                </div>
                <div style={{ textAlign:'center' }}>
                  <button onClick={()=>navigate(`/voter/${v.id}`)} style={{ background:'linear-gradient(135deg,#E55100,#FF6B00)', border:'none', borderRadius:8, padding:'6px 10px', cursor:'pointer', color:'white', fontSize:'0.82rem', fontWeight:700 }}>
                    🖨️
                  </button>
                </div>
              </div>
            ))}
          </div>
          {pages > 1 && (
            <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:12 }}>
              <button onClick={()=>loadVoters(selectedBooth, page-1)} disabled={page===1} className="page-btn">‹</button>
              <span style={{ fontSize:'0.82rem', color:'#9E6040', padding:'0 8px', lineHeight:'38px' }}>Page {page}/{pages}</span>
              <button onClick={()=>loadVoters(selectedBooth, page+1)} disabled={page===pages} className="page-btn">›</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin, isAdmin, isUser } = useAuth();
  const [tab, setTab] = useState('profile');
  const [pwdForm, setPwdForm] = useState({ currentPassword:'', newPassword:'', confirm:'' });
  const [saving, setSaving] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoRef = useRef();

  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [importHistory, setImportHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [deletingBatch, setDeletingBatch] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    axios.get('/users/profile-photo').then(r=>setProfilePhoto(r.data.photo)).catch(()=>{});
  }, []);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const { data } = await axios.get('/voters/import-history');
      setImportHistory(data);
    } catch {}
    finally { setLoadingHistory(false); }
  }, []);

  useEffect(() => {
    if (tab === 'import') fetchHistory();
  }, [tab]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2*1024*1024) { toast.error('Photo must be under 2MB'); return; }
    setUploadingPhoto(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        await axios.put('/users/profile-photo', { photo: ev.target.result });
        setProfilePhoto(ev.target.result);
        toast.success('✅ Profile photo updated!');
      } catch { toast.error('Upload failed'); }
      finally { setUploadingPhoto(false); }
    };
    reader.readAsDataURL(file);
  };

  const handleChangePwd = async () => {
    if (!pwdForm.currentPassword||!pwdForm.newPassword) { toast.error('Fill all fields'); return; }
    if (pwdForm.newPassword!==pwdForm.confirm) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await axios.post('/auth/change-password',{currentPassword:pwdForm.currentPassword,newPassword:pwdForm.newPassword});
      toast.success('✅ Password changed!');
      setPwdForm({currentPassword:'',newPassword:'',confirm:''});
    } catch(err){ toast.error(err.response?.data?.error||'Failed'); }
    finally { setSaving(false); }
  };

  const handleImport = async () => {
    if (!importFile) { toast.error('Select Excel file'); return; }
    setImporting(true); setImportResult(null);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const { data } = await axios.post('/voters/import/excel', formData, {
        headers:{'Content-Type':'multipart/form-data'}, timeout:300000
      });
      setImportResult(data);
      setImportFile(null);
      if (data.hasDuplicates) {
        toast.success(`✅ ${data.imported} imported | ⚠️ ${data.skipped} duplicates skipped`, {duration:5000});
      } else {
        toast.success(`✅ ${data.imported} voters imported!`);
      }
      fetchHistory(); // Refresh history
    } catch(err){ toast.error(err.response?.data?.error||'Import failed'); }
    finally { setImporting(false); }
  };

  const handleClearAll = async () => {
    if (!window.confirm('⚠️ Delete ALL voter records?\n\nThis cannot be undone!')) return;
    if (!window.confirm('FINAL WARNING: All data will be permanently deleted!')) return;
    setClearing(true);
    try {
      const { data } = await axios.delete('/voters/clear-all');
      toast.success(`🗑️ Deleted ${data.deleted.toLocaleString()} records!`);
      fetchHistory();
    } catch { toast.error('Clear failed'); }
    finally { setClearing(false); }
  };

  const handleDeleteBatch = async (batchId, filename, count) => {
    if (!window.confirm(`Delete ${count.toLocaleString()} records from "${filename}"?\n\nThis cannot be undone!`)) return;
    setDeletingBatch(batchId);
    try {
      const { data } = await axios.delete(`/voters/import-history/${batchId}`);
      toast.success(`🗑️ Deleted ${data.deleted} records from "${filename}"`);
      fetchHistory();
    } catch { toast.error('Delete failed'); }
    finally { setDeletingBatch(null); }
  };

  const formatDate = (ts) => {
    const d = new Date(String(ts).replace(' ','T')+(String(ts).includes('Z')?'':'Z'));
    return d.toLocaleString('en-IN', { timeZone:'Asia/Kolkata', day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });
  };

  const tabs = [
    {id:'profile',  label:'👤 Profile'},
    ...(!isUser ? [{id:'password', label:'🔒 Password'}] : []),
    {id:'history',  label:'📋 Import History'},
    {id:'printer',  label:'🖨️ Printer'},
  ];

  const IS = {width:'100%',padding:'11px 14px',border:'2px solid rgba(255,107,0,0.2)',borderRadius:12,fontSize:'0.88rem',fontFamily:"'Outfit',sans-serif",outline:'none',transition:'all 0.2s',background:'rgba(255,255,255,0.8)'};
  const fo = e=>{e.target.style.borderColor='#FF6B00';e.target.style.boxShadow='0 0 0 3px rgba(255,107,0,0.1)';e.target.style.background='white';};
  const bl = e=>{e.target.style.borderColor='rgba(255,107,0,0.2)';e.target.style.boxShadow='none';e.target.style.background='rgba(255,255,255,0.8)';};

  return (
    <div style={{minHeight:'100vh',background:'linear-gradient(160deg,#FFF4ED,#FFF8F0)',fontFamily:"'Outfit',sans-serif"}}>
      <div className="app-header">
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <button className="back-btn" onClick={()=>navigate('/')}>←</button>
          <h1 style={{fontFamily:"'Baloo 2',sans-serif",fontWeight:700,fontSize:'1.1rem',color:'white'}}>Settings</h1>
        </div>
      </div>

      <div style={{display:'flex',gap:8,padding:'12px 16px',background:'rgba(255,255,255,0.6)',backdropFilter:'blur(10px)',borderBottom:'1px solid rgba(255,107,0,0.08)',overflowX:'auto'}}>
        {tabs.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{padding:'6px 14px',borderRadius:99,border:'none',cursor:'pointer',fontWeight:700,fontSize:'0.78rem',whiteSpace:'nowrap',background:tab===t.id?'linear-gradient(135deg,#E55100,#FF6B00)':'rgba(255,255,255,0.85)',color:tab===t.id?'white':'#5C3010',boxShadow:tab===t.id?'0 3px 10px rgba(255,107,0,0.35)':'0 1px 6px rgba(0,0,0,0.05)',transition:'all 0.2s'}}>{t.label}</button>
        ))}
      </div>

      <div style={{maxWidth:700,margin:'0 auto',padding:'20px 16px'}}>

        {/* PROFILE */}
        {tab==='profile'&&(
          <div style={{background:'rgba(255,255,255,0.9)',backdropFilter:'blur(12px)',borderRadius:20,padding:'24px',boxShadow:'0 4px 24px rgba(255,107,0,0.08)',border:'1px solid rgba(255,255,255,0.7)'}}>
            <div style={{textAlign:'center',marginBottom:20}}>
              <div style={{position:'relative',display:'inline-block'}}>
                {profilePhoto
                  ? <img src={profilePhoto} alt="Profile" style={{width:88,height:88,borderRadius:'50%',objectFit:'cover',border:'3px solid rgba(255,107,0,0.3)',boxShadow:'0 4px 16px rgba(255,107,0,0.2)'}}/>
                  : <div style={{width:88,height:88,borderRadius:'50%',background:'linear-gradient(135deg,#E55100,#FF6B00)',display:'flex',alignItems:'center',justifyContent:'center',fontWeight:800,fontSize:'2rem',color:'white',margin:'0 auto',boxShadow:'0 4px 16px rgba(255,107,0,0.35)'}}>{user?.name?.charAt(0)}</div>
                }
                <button onClick={()=>photoRef.current.click()} disabled={uploadingPhoto} style={{position:'absolute',bottom:0,right:0,width:28,height:28,borderRadius:'50%',background:'linear-gradient(135deg,#E55100,#FF6B00)',border:'2px solid white',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.75rem',boxShadow:'0 2px 8px rgba(0,0,0,0.2)'}}>
                  {uploadingPhoto?'⏳':'📷'}
                </button>
                <input ref={photoRef} type="file" accept="image/*" style={{display:'none'}} onChange={handlePhotoUpload}/>
              </div>
              <div style={{fontWeight:800,fontSize:'1.1rem',marginTop:10,color:'#1A0A00'}}>{user?.name}</div>
              <div style={{color:'#9E6040',fontSize:'0.82rem'}}>{user?.email||user?.phone}</div>
              <span style={{marginTop:5,display:'inline-block',background:'rgba(255,107,0,0.1)',color:'#E55100',padding:'2px 10px',borderRadius:99,fontSize:'0.7rem',fontWeight:700,textTransform:'uppercase'}}>{user?.role}</span>
              <div style={{fontSize:'0.7rem',color:'#BDBDBD',marginTop:5}}>Click 📷 to change photo (max 2MB)</div>
            </div>
            <div style={{height:1,background:'rgba(255,107,0,0.08)',margin:'0 0 14px'}}/>
            {[['Name',user?.name],['Email',user?.email||'—'],['Phone',user?.phone||'—'],['Role',user?.role]].map(([k,v])=>(
              <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'9px 0',borderBottom:'1px solid rgba(255,107,0,0.06)'}}>
                <span style={{fontSize:'0.78rem',color:'#9E6040'}}>{k}</span>
                <span style={{fontSize:'0.88rem',fontWeight:600,color:'#1A0A00',textTransform:'capitalize'}}>{v}</span>
              </div>
            ))}
            <button onClick={logout} style={{width:'100%',marginTop:20,padding:'12px',border:'none',borderRadius:12,background:'linear-gradient(135deg,#B71C1C,#E53935)',color:'white',fontWeight:700,fontSize:'0.9rem',cursor:'pointer',fontFamily:"'Outfit',sans-serif",boxShadow:'0 4px 12px rgba(183,28,28,0.3)'}}>
              🚪 Logout
            </button>
          </div>
        )}

        {/* PASSWORD */}
        {tab==='password'&&(
          <div style={{background:'rgba(255,255,255,0.9)',backdropFilter:'blur(12px)',borderRadius:20,padding:'24px',boxShadow:'0 4px 24px rgba(255,107,0,0.08)',border:'1px solid rgba(255,255,255,0.7)'}}>
            <div style={{fontFamily:"'Baloo 2',sans-serif",fontSize:'1rem',fontWeight:700,color:'#BF360C',marginBottom:18}}>🔒 Change Password</div>
            {['Current Password','New Password','Confirm New Password'].map((l,i)=>(
              <div key={l} style={{marginBottom:14}}>
                <label style={{display:'block',fontSize:'0.72rem',fontWeight:700,color:'#BF360C',marginBottom:5,textTransform:'uppercase',letterSpacing:'0.7px'}}>{l}</label>
                <input type="password" style={IS} placeholder={`Enter ${l.toLowerCase()}`}
                value={i===0?pwdForm.currentPassword:i===1?pwdForm.newPassword:pwdForm.confirm}
                onChange={e=>setPwdForm(p=>({...p,[i===0?'currentPassword':i===1?'newPassword':'confirm']:e.target.value}))}
                onFocus={fo} onBlur={bl}/>
              </div>
            ))}
            <button onClick={handleChangePwd} disabled={saving} style={{width:'100%',padding:'12px',border:'none',borderRadius:12,background:'linear-gradient(135deg,#E55100,#FF6B00)',color:'white',fontWeight:700,fontSize:'0.9rem',cursor:'pointer',fontFamily:"'Outfit',sans-serif",boxShadow:'0 4px 12px rgba(255,107,0,0.35)'}}>
              {saving?'Changing...':'✅ Change Password'}
            </button>
          </div>
        )}

        {/* IMPORT */}
        {tab==='import'&&(
          <div>
            {/* Import Section */}
            <div style={{background:'rgba(255,255,255,0.9)',backdropFilter:'blur(12px)',borderRadius:20,padding:'22px',marginBottom:16,boxShadow:'0 4px 24px rgba(255,107,0,0.08)',border:'1px solid rgba(255,255,255,0.7)'}}>
              <div style={{fontFamily:"'Baloo 2',sans-serif",fontSize:'1rem',fontWeight:700,color:'#BF360C',marginBottom:14}}>📥 Import New Data</div>

              <div style={{background:'rgba(255,107,0,0.06)',borderRadius:10,padding:'10px 14px',marginBottom:12,fontSize:'0.78rem',color:'#BF360C',lineHeight:1.7}}>
                <strong>Required columns:</strong> Part No | SrNo | Name | Relative | Sex | Age | Mobile | EPIC | Address | PS | PSName | Dist
              </div>

              <div onClick={()=>fileRef.current.click()} style={{border:`2px dashed ${importFile?'#FF6B00':'rgba(255,107,0,0.25)'}`,borderRadius:14,padding:'20px',textAlign:'center',cursor:'pointer',marginBottom:12,background:importFile?'rgba(255,107,0,0.04)':'white',transition:'all 0.2s'}}>
                <div style={{fontSize:'1.8rem',marginBottom:6}}>📊</div>
                {importFile ? (
                  <><div style={{fontWeight:700,color:'#E55100',fontSize:'0.88rem'}}>{importFile.name}</div>
                  <div style={{color:'#9E6040',fontSize:'0.72rem',marginTop:2}}>{(importFile.size/1024/1024).toFixed(2)} MB</div></>
                ) : (
                  <><div style={{fontWeight:600,color:'#5C3010'}}>Click to select Excel file</div>
                  <div style={{color:'#9E6040',fontSize:'0.72rem',marginTop:2}}>.xlsx or .xls files only</div></>
                )}
                <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={e=>{setImportFile(e.target.files[0]);setImportResult(null);}}/>
              </div>

              <button onClick={handleImport} disabled={importing||!importFile} style={{width:'100%',padding:'12px',border:'none',borderRadius:12,background:importing||!importFile?'#ccc':'linear-gradient(135deg,#E55100,#FF6B00)',color:'white',fontWeight:700,fontSize:'0.9rem',cursor:importing||!importFile?'not-allowed':'pointer',fontFamily:"'Outfit',sans-serif",display:'flex',alignItems:'center',justifyContent:'center',gap:8}}>
                {importing?<><span style={{width:14,height:14,border:'2px solid rgba(255,255,255,0.4)',borderTopColor:'white',borderRadius:'50%',display:'inline-block',animation:'spin 0.7s linear infinite'}}/>Importing... Do not close</>:'📥 Start Import'}
              </button>

              {/* Import Result */}
              {importResult&&(
                <div style={{marginTop:12,background:importResult.hasDuplicates?'#FFF9C4':'#E8F5E9',borderRadius:12,padding:'14px',border:`1.5px solid ${importResult.hasDuplicates?'#FFB300':'#4CAF50'}`}}>
                  <div style={{fontWeight:700,color:importResult.hasDuplicates?'#E65100':'#2E7D32',fontSize:'0.9rem',marginBottom:8}}>
                    {importResult.hasDuplicates?'⚠️ Completed with Duplicates':'✅ Import Complete!'}
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
                    {[['Total',importResult.total,'#5C3010'],['✅ Imported',importResult.imported,'#2E7D32'],['⚠️ Skipped',importResult.skipped,'#E65100']].map(([k,v,c])=>(
                      <div key={k} style={{background:'white',borderRadius:8,padding:'8px',textAlign:'center'}}>
                        <div style={{fontWeight:800,fontSize:'1rem',color:c}}>{(v||0).toLocaleString()}</div>
                        <div style={{fontSize:'0.65rem',color:'#9E6040'}}>{k}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Import History Section */}
            <div style={{background:'rgba(255,255,255,0.9)',backdropFilter:'blur(12px)',borderRadius:20,padding:'22px',boxShadow:'0 4px 24px rgba(255,107,0,0.08)',border:'1px solid rgba(255,255,255,0.7)'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
                <div style={{fontFamily:"'Baloo 2',sans-serif",fontSize:'1rem',fontWeight:700,color:'#BF360C'}}>
                  📁 Import History ({importHistory.length} files)
                </div>
                <button onClick={handleClearAll} disabled={clearing} style={{padding:'6px 14px',border:'none',borderRadius:99,background:clearing?'#ccc':'linear-gradient(135deg,#B71C1C,#E53935)',color:'white',fontWeight:700,fontSize:'0.75rem',cursor:clearing?'not-allowed':'pointer',boxShadow:'0 2px 8px rgba(183,28,28,0.35)'}}>
                  {clearing?'Deleting...':'🗑️ Delete All'}
                </button>
              </div>

              {loadingHistory?(
                <div style={{textAlign:'center',padding:'20px',color:'#9E6040'}}>Loading history...</div>
              ) : importHistory.length===0?(
                <div style={{textAlign:'center',padding:'24px',color:'#9E6040',fontSize:'0.85rem'}}>
                  📭 No import history yet. Import your first Excel file above.
                </div>
              ) : (
                importHistory.map(h=>(
                  <div key={h.batch_id} style={{background:'#FAFAFA',borderRadius:14,padding:'14px 16px',marginBottom:10,border:'1.5px solid rgba(255,107,0,0.1)',transition:'all 0.2s'}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(255,107,0,0.25)';e.currentTarget.style.background='white';}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='rgba(255,107,0,0.1)';e.currentTarget.style.background='#FAFAFA';}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8}}>
                      <div style={{flex:1,minWidth:0}}>
                        {/* Filename */}
                        <div style={{fontWeight:700,fontSize:'0.9rem',color:'#1A0A00',marginBottom:4,display:'flex',alignItems:'center',gap:6}}>
                          <span style={{fontSize:'1.1rem'}}>📄</span>
                          <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{h.filename||'Unknown file'}</span>
                        </div>
                        {/* Date & Time */}
                        <div style={{fontSize:'0.75rem',color:'#FF6B00',fontWeight:600,marginBottom:6}}>
                          🕐 {formatDate(h.imported_at)}
                        </div>
                        {/* Stats */}
                        <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:4}}>
                          <span style={{background:'#E8F5E9',color:'#2E7D32',padding:'2px 9px',borderRadius:99,fontSize:'0.72rem',fontWeight:700}}>✅ {(h.imported||0).toLocaleString()} imported</span>
                          {h.skipped>0&&<span style={{background:'#FFF9C4',color:'#E65100',padding:'2px 9px',borderRadius:99,fontSize:'0.72rem',fontWeight:700}}>⚠️ {h.skipped} skipped</span>}
                          <span style={{background:'rgba(255,107,0,0.1)',color:'#BF360C',padding:'2px 9px',borderRadius:99,fontSize:'0.72rem',fontWeight:700}}>📊 Total: {(h.total_in_file||0).toLocaleString()}</span>
                        </div>
                        {/* District & Booth info */}
                        {h.district_info&&<div style={{fontSize:'0.72rem',color:'#9E6040'}}>🗺️ {h.district_info}</div>}
                        {h.booth_range&&<div style={{fontSize:'0.72rem',color:'#9E6040'}}>🏫 {h.booth_range}</div>}
                        <div style={{fontSize:'0.7rem',color:'#BDBDBD',marginTop:3}}>By: {h.imported_by_name}</div>
                      </div>
                      {/* Delete this batch */}
                      <button
                        onClick={()=>handleDeleteBatch(h.batch_id, h.filename, h.imported)}
                        disabled={deletingBatch===h.batch_id}
                        style={{flexShrink:0,padding:'7px 12px',border:'none',borderRadius:10,background:deletingBatch===h.batch_id?'#ccc':'#FFEBEE',color:'#C62828',fontWeight:700,fontSize:'0.75rem',cursor:deletingBatch===h.batch_id?'not-allowed':'pointer',transition:'all 0.2s',display:'flex',alignItems:'center',gap:4,whiteSpace:'nowrap'}}
                        onMouseEnter={e=>{if(deletingBatch!==h.batch_id){e.currentTarget.style.background='linear-gradient(135deg,#B71C1C,#E53935)';e.currentTarget.style.color='white';}}}
                        onMouseLeave={e=>{e.currentTarget.style.background='#FFEBEE';e.currentTarget.style.color='#C62828';}}>
                        {deletingBatch===h.batch_id?'Deleting...':'🗑️ Delete'}
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* PRINTER */}
        {tab==='history'&&(
          <ImportHistoryTab isAdmin={isAdmin}/>
        )}

        {tab==='printer'&&(
          <PrinterTab navigate={navigate}/>
        )}

        {/* ABOUT */}
        {tab==='about'&&(
          <div style={{background:'rgba(255,255,255,0.9)',backdropFilter:'blur(12px)',borderRadius:20,padding:'24px',textAlign:'center',boxShadow:'0 4px 24px rgba(255,107,0,0.08)',border:'1px solid rgba(255,255,255,0.7)'}}>
            <div style={{fontSize:'2.5rem',marginBottom:10}}>🪷</div>
            <div style={{fontFamily:"'Baloo 2',sans-serif",fontSize:'1.2rem',fontWeight:800,color:'#E55100'}}>Voter App</div>
            <div style={{color:'#9E6040',fontSize:'0.82rem',marginBottom:16}}>Version 1.0 • May 2026</div>
            <div style={{height:1,background:'rgba(255,107,0,0.08)',margin:'0 0 14px'}}/>
            {[['Platform','Android APK + iOS PWA'],['Backend','Node.js + Express + MySQL'],['Frontend','React PWA'],['Hosting','Hostinger Business'],['Database','u694145732_voter_app']].map(([k,v])=>(
              <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid rgba(255,107,0,0.05)'}}>
                <span style={{fontSize:'0.78rem',color:'#9E6040'}}>{k}</span>
                <span style={{fontSize:'0.82rem',fontWeight:600,color:'#1A0A00'}}>{v}</span>
              </div>
            ))}
            <div style={{marginTop:14,padding:'10px',background:'rgba(255,107,0,0.06)',borderRadius:10,fontSize:'0.72rem',color:'#BF360C',fontWeight:600}}>Powered by Influfly • Confidential</div>
          </div>
        )}
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}